/**
 * Created: 2026-10-21
 * Initial commit: Coach calendar page in the web coach app
 *
 * The coach's view of the current site's slot usage and appointments: the
 * admin slots page's grid, drag selection and dialogs, with the actions of the
 * mini coach calendar. The coach selects open court-hours (one court per hour,
 * consecutive hours) and creates an appointment for themselves with 约课; a
 * click on an appointment opens it. The coach's own appointments show green.
 * Used slots other than appointments are greyed out, with no detail.
 * Updated: 2026-10-24 — Reserved slots (reservations and events) show as on
 * the admin slots page; only bookings and locks are greyed out.
 */

import RefreshIcon from '@mui/icons-material/Refresh'
import { Alert, Box, Button, IconButton, Typography } from '@mui/material'
import { isValidCoachSelection } from '@tc-app/shared/coaching/appointment'
import { SlotStatus } from '@tc-app/shared/slots/types'
import { format } from 'date-fns'
import { useMemo, useState } from 'react'
import AppointmentDialog from '../components/AppointmentDialog'
import CreateAppointmentDialog from '../components/CreateAppointmentDialog'
import DateNavigation from '../components/DateNavigation'
import { useCurrentUser, useRole } from '../hooks'
import SlotGrid, { DRAG_HIGHLIGHT_CLASS } from '../pages/SlotsPage/SlotGrid'
import { useDragSelection } from '../pages/SlotsPage/useDragSelection'
import { useSiteSlots } from '../pages/SlotsPage/useSiteSlots'

const LEGEND: { label: string; bg: string; color: string }[] = [
  { label: '我的课程', bg: '#e8f5e9', color: '#2e7d32' },
  { label: '其他课程/保留', bg: '#fff3e0', color: '#e65100' },
  { label: '活动', bg: '#ede7f6', color: '#4527a0' },
  { label: '已占用', bg: '#eeeeee', color: '#757575' },
]

export default function CoachCalendarPage() {
  const { currentSite } = useRole()
  const [currentUser] = useCurrentUser()
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())

  const {
    courts,
    slotLookup,
    hours,
    spanBlocks,
    coveredCells,
    spanKeyMap,
    spanGroupCells,
    pastHourThreshold,
    refetchSlots,
    loading,
    error,
  } = useSiteSlots(currentSite?.id, selectedDate)

  const courtIds = useMemo(() => courts.map((c) => c.id), [courts])

  const {
    selectedCells,
    setSelectedCells,
    selectedKeys,
    gridRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  } = useDragSelection({
    courtIds,
    pastHourThreshold,
    slotLookup,
    spanKeyMap,
    spanGroupCells,
    dragHighlightClass: DRAG_HIGHLIGHT_CLASS,
  })

  const [sessionDialogId, setSessionDialogId] = useState<string | null>(null)
  const [createAppointmentOpen, setCreateAppointmentOpen] = useState(false)

  const handleDateChange = (newDate: Date) => {
    setSelectedDate(newDate)
    setSelectedCells([])
  }

  // As on the admin slots page and the mini coach calendar: every cell open,
  // one court per hour, consecutive hours. Courts of any sport may be mixed.
  const courtById = new Map(courts.map((c) => [c.id, c]))
  const appointmentSlots = [...selectedCells]
    .sort((a, b) => a.hour - b.hour)
    .map((c) => ({
      courtId: c.courtId,
      courtName: courtById.get(c.courtId)?.name ?? '',
      hour: c.hour,
    }))
  const canAppoint =
    selectedCells.every(
      (c) => slotLookup.get(`${c.hour}-${c.courtId}`)?.status === SlotStatus.Open,
    ) && isValidCoachSelection(appointmentSlots)

  // Only appointments open on click; other used slots show no detail.
  const handleCellClick = (hour: number, courtId: string) => {
    const sessionId = slotLookup.get(`${hour}-${courtId}`)?.sessionId
    if (sessionId) setSessionDialogId(sessionId)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 48px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography variant="h4">课程日历</Typography>
        <DateNavigation value={selectedDate} onChange={handleDateChange} />
      </Box>

      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={!canAppoint || !currentUser?.id}
            onClick={() => setCreateAppointmentOpen(true)}
          >
            约课
          </Button>
          <IconButton size="small" onClick={refetchSlots} sx={{ ml: 0.5 }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto' }}>
          {LEGEND.map((item) => (
            <Box
              key={item.label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.75rem',
              }}
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '2px',
                  backgroundColor: item.bg,
                  border: '1px solid #ccc',
                }}
              />
              <Typography variant="caption" sx={{ color: item.color }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {error && <Alert severity="error">加载场地数据失败: {error.message}</Alert>}

      <SlotGrid
        courts={courts}
        hours={hours}
        slotLookup={slotLookup}
        coveredCells={coveredCells}
        spanBlocks={spanBlocks}
        pastHourThreshold={pastHourThreshold}
        selectedKeys={selectedKeys}
        loading={loading}
        gridRef={gridRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onCellClick={handleCellClick}
        currentCoachId={currentUser?.id ?? undefined}
      />

      <AppointmentDialog
        sessionId={sessionDialogId}
        onClose={() => setSessionDialogId(null)}
        onChanged={refetchSlots}
      />

      {createAppointmentOpen && currentSite && currentUser?.id && (
        <CreateAppointmentDialog
          siteId={currentSite.id}
          date={format(selectedDate, 'yyyy-MM-dd')}
          slots={appointmentSlots}
          coachId={currentUser.id}
          onClose={() => setCreateAppointmentOpen(false)}
          onSuccess={(sessionId) => {
            setCreateAppointmentOpen(false)
            setSelectedCells([])
            refetchSlots()
            setSessionDialogId(sessionId)
          }}
        />
      )}
    </Box>
  )
}
